import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../AuthContext';
import { useEnvironment } from '../contexts/EnvironmentContext';
import api, { extractApiError } from '../api';
import {
  Box, Typography, Tabs, Tab, Card, CardContent, Stack, Button, Alert,
  CircularProgress, TextField, MenuItem, Select, FormControl, InputLabel,
  Table, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle,
  DialogContent, DialogActions, Snackbar, Chip, IconButton, Autocomplete,
  FormControlLabel, Checkbox, List, ListItem, ListItemText,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Publish as PublishIcon, Refresh as RefreshIcon,
  Autorenew as ForceRestockIcon, ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon,
  VerticalAlignTop as SendToTopIcon, VerticalAlignBottom as SendToBottomIcon,
  Download as DownloadIcon, Upload as UploadIcon,
} from '@mui/icons-material';
import {
  TRADER_ASSET_PICKER_HELPER,
  filterAssetOptions,
  filterSelectableTraderAssets,
  getAssetPickerLabel,
  getUnityTypeChipProps,
  truncateGuid,
} from '../utils/assetUtils';

const TAB_DEFAULT_SET = 0;
const TAB_DEAL_LIBRARY = 1;
const TAB_EVENT_CATALOGS = 2;
const TAB_PREVIEW = 3;

const MERCH_TYPES = ['Food', 'Clothes', 'CraftingMaterial', 'Other', 'Quest', 'Minting', 'Blueprints'];
const TRADER_TAB_TYPES = MERCH_TYPES;
const MOANI_BOARD_TAB = 'MoaniBoard';

function resolveMerchandiseTab(deal, isMoaniBoard) {
  if (isMoaniBoard || deal?.catalogKind === 'moani_board' || deal?.merchandiseType === MOANI_BOARD_TAB) {
    return MOANI_BOARD_TAB;
  }
  const tab = deal?.merchandiseType || 'Other';
  return TRADER_TAB_TYPES.includes(tab) ? tab : 'Other';
}

function groupDefaultEntries(entries, dealMap, isMoaniBoard) {
  if (isMoaniBoard) {
    return { [MOANI_BOARD_TAB]: [...entries] };
  }
  const groups = Object.fromEntries(TRADER_TAB_TYPES.map((t) => [t, []]));
  for (const entry of entries) {
    const deal = dealMap.get(entry.dealTemplateId);
    groups[resolveMerchandiseTab(deal, false)].push(entry);
  }
  return groups;
}

function flattenGroupedDefaultEntries(groups, tabOrder) {
  const result = [];
  for (const tab of tabOrder) {
    result.push(...(groups[tab] || []));
  }
  return result;
}

function groupDealsByTraderTab(deals) {
  const groups = Object.fromEntries(TRADER_TAB_TYPES.map((t) => [t, []]));
  for (const deal of deals) {
    groups[resolveMerchandiseTab(deal, false)].push(deal);
  }
  return groups;
}
const emptyDeal = {
  title: '', catalogKind: 'trader', merchandiseType: 'Other',
  assetId: '', amount: 1, priceAssetId: '', priceAmount: 1, defaultStackAmount: 1,
  hideIfItemsInInventoryIds: [], preventBuyingIfItemNotInInventory: '', notes: '',
};

function catalogKindLabel(kind) {
  if (kind === 'moani_board') return 'Moani Board';
  if (kind === 'both') return 'Both';
  return 'Trader';
}

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function AssetTypeChip({ asset, sx }) {
  if (!asset) return null;
  const chipProps = getUnityTypeChipProps(asset);
  return <Chip {...chipProps} sx={{ ...chipProps.sx, ...sx }} />;
}

function DealAssetSummary({ amount, assetId, assetMap, fallbackId }) {
  const asset = assetMap.get(assetId);
  if (!asset) {
    return <Typography variant="body2" component="span">{fallbackId || assetId || 'Unknown item'}</Typography>;
  }
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
      <Typography variant="body2" component="span">
        {amount != null ? `${amount}× ` : ''}{asset.title || asset.id}
      </Typography>
      <AssetTypeChip asset={asset} />
    </Stack>
  );
}

function TraderCatalogManager() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { environment, isDevelopment } = useEnvironment();
  const importFileRef = useRef(null);
  const [tab, setTab] = useState(TAB_DEFAULT_SET);
  const [selectedTraderId, setSelectedTraderId] = useState('');
  const [dealDialog, setDealDialog] = useState({ open: false, deal: emptyDeal, editing: false });
  const [eventDialog, setEventDialog] = useState({
    open: false, name: '', startDate: '', endDate: '', priority: 0, dealTemplateIds: [],
  });
  const [importDialog, setImportDialog] = useState({
    open: false,
    bundle: null,
    fileName: '',
    importDeals: true,
    importDefaultSets: true,
    importEventCatalogs: true,
    selectedTraderOnly: false,
  });
  const [importPreview, setImportPreview] = useState(null);
  const [importPreviewLoading, setImportPreviewLoading] = useState(false);
  const [importPreviewError, setImportPreviewError] = useState('');
  const [dealLibraryFilter, setDealLibraryFilter] = useState('all');
  const [previewDate, setPreviewDate] = useState(new Date().toISOString().slice(0, 10));
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { data: traders = [], isLoading: tradersLoading } = useQuery({
    queryKey: ['traderCatalogTraders'],
    queryFn: async () => (await api.get('/trader-catalog/traders')).data,
  });

  const selectedTrader = traders.find((t) => t.gridObjectId === selectedTraderId) || traders[0];
  const gridObjectId = selectedTrader?.gridObjectId || '';

  React.useEffect(() => {
    if (!selectedTraderId && traders[0]) setSelectedTraderId(traders[0].gridObjectId);
  }, [traders, selectedTraderId]);

  const { data: deals = [], isLoading: dealsLoading } = useQuery({
    queryKey: ['traderCatalogDeals'],
    queryFn: async () => (await api.get('/trader-catalog/deals')).data,
  });

  const { data: assets = [] } = useQuery({
    queryKey: ['allAssets'],
    queryFn: async () => (await api.get('/inventories/items')).data,
    staleTime: 5 * 60 * 1000,
  });

  const assetMap = useMemo(() => {
    const map = new Map();
    for (const asset of assets) map.set(asset.id, asset);
    return map;
  }, [assets]);

  const getAssetName = (assetId) => {
    if (!assetId) return 'Unknown item';
    const asset = assetMap.get(assetId);
    if (!asset) return assetId;
    return getAssetPickerLabel(asset);
  };

  const formatDealSummary = (deal) => `${deal.amount}× ${getAssetName(deal.assetId)}`;

  const dealOptionLabel = (deal) => formatDealSummary(deal);

  const traderTabLabel = (deal) => (
    deal.catalogKind === 'moani_board' || deal.merchandiseType === 'MoaniBoard'
      ? null
      : deal.merchandiseType
  );

  const selectableTraderAssets = useMemo(
    () => filterSelectableTraderAssets(assets),
    [assets]
  );

  const selectedSoldAsset = useMemo(
    () => assets.find((a) => a.id === dealDialog.deal.assetId) || null,
    [assets, dealDialog.deal.assetId]
  );

  const selectedCostAsset = useMemo(
    () => assets.find((a) => a.id === dealDialog.deal.priceAssetId) || null,
    [assets, dealDialog.deal.priceAssetId]
  );

  const renderAssetOption = (props, option) => (
    <li {...props} key={option.id}>
      <Stack spacing={0.25} sx={{ py: 0.25 }}>
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2">{option.title || option.id}</Typography>
          <AssetTypeChip asset={option} />
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {option.inventoryType && option.inventoryType !== 'default' && (
            <Chip label={option.inventoryType} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {truncateGuid(option.id)}
          </Typography>
        </Stack>
      </Stack>
    </li>
  );

  const renderAssetPreview = (asset) => {
    if (!asset) return null;
    return (
      <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" fontWeight={600}>{asset.title || asset.id}</Typography>
          <AssetTypeChip asset={asset} />
        </Stack>
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
          {asset.inventoryType && asset.inventoryType !== 'default' && (
            <Chip label={`inv: ${asset.inventoryType}`} size="small" variant="outlined" />
          )}
          {asset.category && (
            <Chip label={asset.category} size="small" variant="outlined" />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block', mt: 0.5 }}>
          {asset.id}
        </Typography>
      </Box>
    );
  };

  const { data: defaultSet, refetch: refetchDefault } = useQuery({
    queryKey: ['traderDefaultSet', gridObjectId, 'draft'],
    queryFn: async () => gridObjectId
      ? (await api.get('/trader-catalog/default-set', { params: { gridObjectId, scope: 'draft' } })).data
      : { entries: [] },
    enabled: !!gridObjectId,
  });

  const { data: events = [], refetch: refetchEvents } = useQuery({
    queryKey: ['traderEvents', gridObjectId, 'draft'],
    queryFn: async () => gridObjectId
      ? (await api.get('/trader-catalog/events', { params: { gridObjectId, scope: 'draft' } })).data
      : [],
    enabled: !!gridObjectId,
  });

  const filteredDeals = useMemo(() => {
    const active = deals.filter((d) => d.isActive);
    if (dealLibraryFilter === 'trader') {
      return active.filter((d) => d.catalogKind === 'trader' || d.catalogKind === 'both');
    }
    if (dealLibraryFilter === 'moani_board') {
      return active.filter((d) => d.catalogKind === 'moani_board' || d.catalogKind === 'both');
    }
    return active;
  }, [deals, dealLibraryFilter]);

  const traderLibraryDeals = useMemo(() => (
    filteredDeals.filter((d) => d.catalogKind === 'trader' || d.catalogKind === 'both')
  ), [filteredDeals]);

  const moaniLibraryDeals = useMemo(() => (
    filteredDeals.filter((d) => d.catalogKind === 'moani_board' || d.catalogKind === 'both')
  ), [filteredDeals]);

  const dealsGroupedByTab = useMemo(
    () => groupDealsByTraderTab(traderLibraryDeals),
    [traderLibraryDeals]
  );

  const dealsForSelectedTrader = useMemo(() => {
    const active = deals.filter((d) => d.isActive);
    if (selectedTrader?.catalogKind === 'moani_board') {
      return active.filter((d) => d.catalogKind === 'moani_board' || d.catalogKind === 'both');
    }
    return active.filter((d) => d.catalogKind === 'trader' || d.catalogKind === 'both');
  }, [deals, selectedTrader?.catalogKind]);

  const dealMap = useMemo(() => {
    const map = new Map();
    for (const deal of deals) map.set(deal.id, deal);
    return map;
  }, [deals]);

  const buildDealPayload = (deal) => ({
    ...deal,
    title: formatDealSummary(deal),
    merchandiseType: deal.catalogKind === 'moani_board' ? 'MoaniBoard' : deal.merchandiseType,
  });

  const { data: preview, refetch: refetchPreview } = useQuery({
    queryKey: ['traderPreview', gridObjectId, previewDate],
    queryFn: async () => gridObjectId
      ? (await api.get('/trader-catalog/preview', {
        params: { gridObjectId, date: previewDate, scope: 'published', catalogKind: selectedTrader?.catalogKind || 'trader' },
      })).data
      : null,
    enabled: !!gridObjectId && tab === TAB_PREVIEW,
  });

  const forceRestockMutation = useMutation({
    mutationFn: async () => (await api.post('/trader-catalog/force-refresh', {
      gridObjectId,
      catalogKind: selectedTrader?.catalogKind || 'trader',
    })).data,
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Force restock triggered for live catalog', severity: 'success' });
    },
    onError: (e) => setSnackbar({
      open: true,
      message: extractApiError(e, 'Force restock failed'),
      severity: 'error',
    }),
  });

  const saveDealMutation = useMutation({
    mutationFn: async (deal) => {
      const payload = buildDealPayload(deal);
      if (dealDialog.editing) {
        return (await api.put(`/trader-catalog/deals/${deal.id}`, payload)).data;
      }
      return (await api.post('/trader-catalog/deals', payload)).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['traderCatalogDeals'] });
      setDealDialog({ open: false, deal: emptyDeal, editing: false });
      setSnackbar({ open: true, message: 'Deal saved', severity: 'success' });
    },
    onError: (e) => setSnackbar({
      open: true,
      message: extractApiError(e, 'Failed to save deal'),
      severity: 'error',
    }),
  });

  const saveDefaultMutation = useMutation({
    mutationFn: async (entries) => (
      await api.put('/trader-catalog/default-set', {
        gridObjectId,
        entries: entries.map((e) => ({ dealTemplateId: e.dealTemplateId, stackAmountOverride: e.stackAmountOverride })),
      })
    ).data,
    onSuccess: () => {
      refetchDefault();
      setSnackbar({ open: true, message: 'Default set saved (draft)', severity: 'success' });
    },
    onError: (e) => setSnackbar({
      open: true,
      message: extractApiError(e, 'Failed to save default set'),
      severity: 'error',
    }),
  });

  const saveEventMutation = useMutation({
    mutationFn: async (payload) => (await api.post('/trader-catalog/events', payload)).data,
    onSuccess: () => {
      refetchEvents();
      setEventDialog({ open: false, name: '', startDate: '', endDate: '', priority: 0, dealTemplateIds: [] });
      setSnackbar({ open: true, message: 'Event catalog created (draft)', severity: 'success' });
    },
    onError: (e) => setSnackbar({
      open: true,
      message: extractApiError(e, 'Failed to create event catalog'),
      severity: 'error',
    }),
  });

  const publishMutation = useMutation({
    mutationFn: async () => (await api.post('/trader-catalog/publish', { gridObjectId })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['traderCatalogTraders'] });
      setSnackbar({ open: true, message: 'Published to live catalog', severity: 'success' });
    },
    onError: (e) => setSnackbar({
      open: true,
      message: extractApiError(e, 'Publish failed'),
      severity: 'error',
    }),
  });

  const downloadCatalogBundle = (bundle, fileName) => {
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const exportCatalog = async ({ scope = 'draft', traderOnly = false } = {}) => {
    try {
      const params = { scope };
      if (traderOnly && gridObjectId) params.gridObjectId = gridObjectId;
      const response = await api.get('/trader-catalog/export', { params });
      const stamp = new Date().toISOString().slice(0, 10);
      const suffix = traderOnly && gridObjectId ? `-${gridObjectId.slice(0, 8)}` : '-all';
      downloadCatalogBundle(response.data, `trader-catalog-${scope}${suffix}-${stamp}.json`);
      setSnackbar({ open: true, message: 'Catalog exported', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: extractApiError(e, 'Export failed'), severity: 'error' });
    }
  };

  const importCatalogMutation = useMutation({
    mutationFn: async (payload) => (await api.post('/trader-catalog/import', payload)).data,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['traderCatalogTraders'] });
      queryClient.invalidateQueries({ queryKey: ['traderCatalogDeals'] });
      queryClient.invalidateQueries({ queryKey: ['traderDefaultSet'] });
      queryClient.invalidateQueries({ queryKey: ['traderEvents'] });
      refetchDefault();
      refetchEvents();
      setImportDialog({
        open: false,
        bundle: null,
        fileName: '',
        importDeals: true,
        importDefaultSets: true,
        importEventCatalogs: true,
        selectedTraderOnly: false,
      });
      setImportPreview(null);
      setImportPreviewError('');
      const { stats, warnings } = result;
      const summary = [
        `${stats.dealsCreated} deals created`,
        `${stats.dealsUpdated} deals updated`,
        `${stats.defaultSetsReplaced} default sets replaced`,
        `${stats.eventsImported} events imported`,
      ].join(', ');
      setSnackbar({
        open: true,
        message: warnings?.length ? `${summary}. ${warnings.length} warning(s).` : `Import complete: ${summary}`,
        severity: warnings?.length ? 'warning' : 'success',
      });
    },
    onError: (e) => setSnackbar({
      open: true,
      message: extractApiError(e, 'Import failed'),
      severity: 'error',
    }),
  });

  const handleImportFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      setImportPreview(null);
      setImportPreviewError('');
      setImportDialog({
        open: true,
        bundle,
        fileName: file.name,
        importDeals: true,
        importDefaultSets: true,
        importEventCatalogs: true,
        selectedTraderOnly: false,
      });
    } catch (error) {
      setSnackbar({ open: true, message: `Invalid JSON file: ${error.message}`, severity: 'error' });
    }
  };

  useEffect(() => {
    if (!importDialog.open || !importDialog.bundle) {
      setImportPreview(null);
      setImportPreviewError('');
      setImportPreviewLoading(false);
      return undefined;
    }

    let cancelled = false;
    setImportPreviewLoading(true);
    setImportPreviewError('');

    api.post('/trader-catalog/import/preview', {
      bundle: importDialog.bundle,
      importDeals: importDialog.importDeals,
      importDefaultSets: importDialog.importDefaultSets,
      importEventCatalogs: importDialog.importEventCatalogs,
      gridObjectId: importDialog.selectedTraderOnly ? gridObjectId : null,
    })
      .then((response) => {
        if (!cancelled) setImportPreview(response.data);
      })
      .catch((error) => {
        if (!cancelled) {
          setImportPreview(null);
          setImportPreviewError(extractApiError(error, 'Failed to load import preview'));
        }
      })
      .finally(() => {
        if (!cancelled) setImportPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    importDialog.open,
    importDialog.bundle,
    importDialog.importDeals,
    importDialog.importDefaultSets,
    importDialog.importEventCatalogs,
    importDialog.selectedTraderOnly,
    gridObjectId,
  ]);

  const [draftDefaultEntries, setDraftDefaultEntries] = useState([]);
  const [addDefaultDealDialog, setAddDefaultDealDialog] = useState({ open: false, deal: null, tab: null });

  const isMoaniBoardTrader = selectedTrader?.catalogKind === 'moani_board';
  const defaultSetTabOrder = isMoaniBoardTrader ? [MOANI_BOARD_TAB] : TRADER_TAB_TYPES;

  React.useEffect(() => {
    if (defaultSet?.entries) {
      setDraftDefaultEntries(defaultSet.entries.map((e) => ({
        dealTemplateId: e.dealTemplateId,
        stackAmountOverride: e.stackAmountOverride ?? '',
      })));
    }
  }, [defaultSet]);

  const draftDefaultDealIds = useMemo(
    () => new Set(draftDefaultEntries.map((e) => e.dealTemplateId)),
    [draftDefaultEntries]
  );

  const addableDefaultDeals = useMemo(
    () => dealsForSelectedTrader.filter((d) => !draftDefaultDealIds.has(d.id)),
    [dealsForSelectedTrader, draftDefaultDealIds]
  );

  const defaultEntriesGrouped = useMemo(
    () => groupDefaultEntries(draftDefaultEntries, dealMap, isMoaniBoardTrader),
    [draftDefaultEntries, dealMap, isMoaniBoardTrader]
  );

  const moveDefaultEntryInTab = (tab, localIndex, direction) => {
    setDraftDefaultEntries((prev) => {
      const grouped = groupDefaultEntries(prev, dealMap, isMoaniBoardTrader);
      const tabEntries = grouped[tab] || [];
      const target = localIndex + direction;
      if (target < 0 || target >= tabEntries.length) return prev;
      [tabEntries[localIndex], tabEntries[target]] = [tabEntries[target], tabEntries[localIndex]];
      grouped[tab] = tabEntries;
      return flattenGroupedDefaultEntries(grouped, defaultSetTabOrder);
    });
  };

  const moveDefaultEntryToEdgeInTab = (tab, localIndex, edge) => {
    setDraftDefaultEntries((prev) => {
      const grouped = groupDefaultEntries(prev, dealMap, isMoaniBoardTrader);
      const tabEntries = [...(grouped[tab] || [])];
      if (localIndex < 0 || localIndex >= tabEntries.length) return prev;
      if (edge === 'top' && localIndex === 0) return prev;
      if (edge === 'bottom' && localIndex === tabEntries.length - 1) return prev;
      const [entry] = tabEntries.splice(localIndex, 1);
      if (edge === 'top') tabEntries.unshift(entry);
      else tabEntries.push(entry);
      grouped[tab] = tabEntries;
      return flattenGroupedDefaultEntries(grouped, defaultSetTabOrder);
    });
  };

  const removeDefaultEntryInTab = (tab, localIndex) => {
    setDraftDefaultEntries((prev) => {
      const grouped = groupDefaultEntries(prev, dealMap, isMoaniBoardTrader);
      grouped[tab] = (grouped[tab] || []).filter((_, i) => i !== localIndex);
      return flattenGroupedDefaultEntries(grouped, defaultSetTabOrder);
    });
  };

  const updateDefaultStackOverrideInTab = (tab, localIndex, value) => {
    setDraftDefaultEntries((prev) => {
      const grouped = groupDefaultEntries(prev, dealMap, isMoaniBoardTrader);
      grouped[tab] = (grouped[tab] || []).map((entry, i) => (
        i === localIndex ? { ...entry, stackAmountOverride: value } : entry
      ));
      return flattenGroupedDefaultEntries(grouped, defaultSetTabOrder);
    });
  };

  const addDefaultEntry = (deal, tab = null) => {
    if (!deal || draftDefaultDealIds.has(deal.id)) return;
    setDraftDefaultEntries((prev) => {
      const grouped = groupDefaultEntries(prev, dealMap, isMoaniBoardTrader);
      const targetTab = tab || resolveMerchandiseTab(deal, isMoaniBoardTrader);
      grouped[targetTab] = [...(grouped[targetTab] || []), { dealTemplateId: deal.id, stackAmountOverride: '' }];
      return flattenGroupedDefaultEntries(grouped, defaultSetTabOrder);
    });
    setAddDefaultDealDialog({ open: false, deal: null, tab: null });
  };

  const addableDefaultDealsForTab = (tab) => addableDefaultDeals.filter(
    (d) => resolveMerchandiseTab(d, isMoaniBoardTrader) === tab
  );

  const renderDefaultSetTable = (tab, entries) => (
    <Box key={tab} sx={{ mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>{tab}</Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={() => setAddDefaultDealDialog({ open: true, deal: null, tab })}
          disabled={addableDefaultDealsForTab(tab).length === 0}
        >
          Add deal
        </Button>
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={168}>Order</TableCell>
            <TableCell>Deal</TableCell>
            <TableCell>Cost</TableCell>
            <TableCell>Stack override</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} sx={{ opacity: 0.7 }}>
                No {tab} deals in this trader&apos;s default set.
              </TableCell>
            </TableRow>
          ) : entries.map((entry, localIndex) => {
            const deal = dealMap.get(entry.dealTemplateId);
            return (
              <TableRow key={`${tab}-${entry.dealTemplateId}-${localIndex}`}>
                <TableCell>
                  <Stack direction="row" spacing={0.25} alignItems="center">
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="Send deal to top"
                      title="Send to top"
                      disabled={localIndex === 0}
                      onClick={() => moveDefaultEntryToEdgeInTab(tab, localIndex, 'top')}
                    >
                      <SendToTopIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="Move deal up"
                      title="Move up"
                      disabled={localIndex === 0}
                      onClick={() => moveDefaultEntryInTab(tab, localIndex, -1)}
                    >
                      <ArrowUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="Move deal down"
                      title="Move down"
                      disabled={localIndex === entries.length - 1}
                      onClick={() => moveDefaultEntryInTab(tab, localIndex, 1)}
                    >
                      <ArrowDownIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="Send deal to bottom"
                      title="Send to bottom"
                      disabled={localIndex === entries.length - 1}
                      onClick={() => moveDefaultEntryToEdgeInTab(tab, localIndex, 'bottom')}
                    >
                      <SendToBottomIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
                <TableCell>
                  {deal ? (
                    <DealAssetSummary amount={deal.amount} assetId={deal.assetId} assetMap={assetMap} />
                  ) : entry.dealTemplateId}
                </TableCell>
                <TableCell>
                  {deal ? (
                    <DealAssetSummary amount={deal.priceAmount} assetId={deal.priceAssetId} assetMap={assetMap} />
                  ) : '—'}
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    placeholder={deal?.defaultStackAmount != null ? String(deal.defaultStackAmount) : 'Default'}
                    value={entry.stackAmountOverride}
                    onChange={(e) => updateDefaultStackOverrideInTab(tab, localIndex, e.target.value)}
                    inputProps={{ min: 1 }}
                    sx={{ width: 120 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    aria-label="Remove deal"
                    onClick={() => removeDefaultEntryInTab(tab, localIndex)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );

  const renderDealLibraryTable = (dealsToShow, showCategory = false) => (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Deal</TableCell>
          <TableCell>Cost</TableCell>
          {showCategory && <TableCell>Category</TableCell>}
          <TableCell align="right">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {dealsToShow.length === 0 ? (
          <TableRow>
            <TableCell colSpan={showCategory ? 4 : 3} sx={{ opacity: 0.7 }}>No deals</TableCell>
          </TableRow>
        ) : dealsToShow.map((d) => (
          <TableRow key={d.id}>
            <TableCell>
              <DealAssetSummary amount={d.amount} assetId={d.assetId} assetMap={assetMap} />
            </TableCell>
            <TableCell>
              <DealAssetSummary amount={d.priceAmount} assetId={d.priceAssetId} assetMap={assetMap} />
            </TableCell>
            {showCategory && (
              <TableCell><Chip size="small" label={catalogKindLabel(d.catalogKind)} /></TableCell>
            )}
            <TableCell align="right">
              <Button size="small" onClick={() => setDealDialog({ open: true, deal: d, editing: true })}>Edit</Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const saveDraftDefaultSet = () => {
    saveDefaultMutation.mutate(draftDefaultEntries.map((entry) => ({
      dealTemplateId: entry.dealTemplateId,
      stackAmountOverride: entry.stackAmountOverride === '' || entry.stackAmountOverride == null
        ? null
        : Number(entry.stackAmountOverride),
    })));
  };

  if (tradersLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4">Trader Catalog</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {['manager', 'admin', 'super_admin'].includes(user?.role) && (
            <>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => exportCatalog({ scope: 'draft', traderOnly: false })}
              >
                Export draft
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                disabled={!gridObjectId}
                onClick={() => exportCatalog({ scope: 'draft', traderOnly: true })}
              >
                Export trader
              </Button>
            </>
          )}
          {['admin', 'super_admin'].includes(user?.role) && (
            <>
              <input
                ref={importFileRef}
                type="file"
                accept="application/json,.json"
                hidden
                onChange={handleImportFileSelected}
              />
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => importFileRef.current?.click()}
              >
                Import to draft
              </Button>
            </>
          )}
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Trader</InputLabel>
            <Select value={gridObjectId} label="Trader" onChange={(e) => setSelectedTraderId(e.target.value)}>
              {traders.map((t) => (
                <MenuItem key={t.gridObjectId} value={t.gridObjectId}>
                  {t.displayName} {t.catalogKind === 'moani_board' ? '(Moani)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {['manager', 'admin', 'super_admin'].includes(user?.role) && (
            <Button
              variant="outlined"
              startIcon={<ForceRestockIcon />}
              onClick={() => forceRestockMutation.mutate()}
              disabled={!gridObjectId || forceRestockMutation.isPending}
            >
              Force restock
            </Button>
          )}
          <Button variant="contained" startIcon={<PublishIcon />} onClick={() => publishMutation.mutate()} disabled={!gridObjectId || publishMutation.isPending}>
            Publish
          </Button>
        </Stack>
      </Stack>

      {selectedTrader && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Environment: {isDevelopment ? 'Development' : 'Production'} ({environment})
          {' · '}Draft v{selectedTrader.draftCatalogVersion} → Published v{selectedTrader.publishedCatalogVersion}
          {' · '}{selectedTrader.restockPolicy === 'calendar_day_utc' ? 'Restock: UTC midnight' : 'Restock: rolling'}
          {' · '}Export from dev, switch to prod, import to draft, then publish.
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Default Set" />
        <Tab label="Deal Library" />
        <Tab label="Event Catalogs" />
        <Tab label="Preview" />
      </Tabs>

      <TabPanel value={tab} index={TAB_DEFAULT_SET}>
        {selectedTrader && (
          <Typography variant="h6" sx={{ mb: 1 }}>
            {selectedTrader.displayName}
            {selectedTrader.catalogKind === 'moani_board' ? ' (Moani Board)' : ''}
          </Typography>
        )}
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
          Default deals shown when no event catalog is active for this trader. Grouped by Unity trader UI tab.
        </Typography>
        {defaultSetTabOrder.map((tabKey) => renderDefaultSetTable(tabKey, defaultEntriesGrouped[tabKey] || []))}
        <Button
          sx={{ mt: 2 }}
          variant="contained"
          onClick={saveDraftDefaultSet}
          disabled={saveDefaultMutation.isPending}
        >
          Save default set (draft)
        </Button>
      </TabPanel>

      <TabPanel value={tab} index={TAB_DEAL_LIBRARY}>
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
          Global deal templates — reused across traders. Grouped by Unity trader UI tab.
        </Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1}>
            {[
              { id: 'all', label: 'All deals' },
              { id: 'trader', label: 'Trader NPCs' },
              { id: 'moani_board', label: 'Moani Board' },
            ].map((filter) => (
              <Chip
                key={filter.id}
                label={filter.label}
                color={dealLibraryFilter === filter.id ? 'primary' : 'default'}
                onClick={() => setDealLibraryFilter(filter.id)}
                variant={dealLibraryFilter === filter.id ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
          <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setDealDialog({
            open: true,
            deal: { ...emptyDeal, catalogKind: dealLibraryFilter === 'moani_board' ? 'moani_board' : 'trader' },
            editing: false,
          })}>
            New deal
          </Button>
        </Stack>
        {dealsLoading ? <CircularProgress /> : (
          <>
            {dealLibraryFilter !== 'moani_board' && TRADER_TAB_TYPES.map((tabKey) => (
              <Box key={tabKey} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>{tabKey}</Typography>
                {renderDealLibraryTable(dealsGroupedByTab[tabKey] || [], dealLibraryFilter === 'all')}
              </Box>
            ))}
            {dealLibraryFilter !== 'trader' && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Moani Board</Typography>
                {renderDealLibraryTable(moaniLibraryDeals, dealLibraryFilter === 'all')}
              </Box>
            )}
          </>
        )}
      </TabPanel>

      <TabPanel value={tab} index={TAB_EVENT_CATALOGS}>
        {selectedTrader && (
          <Typography variant="h6" sx={{ mb: 1 }}>
            {selectedTrader.displayName}
            {selectedTrader.catalogKind === 'moani_board' ? ' (Moani Board)' : ''}
          </Typography>
        )}
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Event catalogs automatically override the default set between start and end dates.
          </Typography>
          <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setEventDialog({
            open: true, name: '', startDate: previewDate, endDate: previewDate, priority: 10, dealTemplateIds: [],
          })}>
            New event
          </Button>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>End</TableCell>
              <TableCell>Deals</TableCell>
              <TableCell>Priority</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((ev) => (
              <TableRow key={ev.id}>
                <TableCell>{ev.name}</TableCell>
                <TableCell>{ev.startDate}</TableCell>
                <TableCell>{ev.endDate}</TableCell>
                <TableCell>{ev.dealCount}</TableCell>
                <TableCell>{ev.priority}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TabPanel>

      <TabPanel value={tab} index={TAB_PREVIEW}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <TextField type="date" label="Date (UTC)" size="small" InputLabelProps={{ shrink: true }}
            value={previewDate} onChange={(e) => setPreviewDate(e.target.value)} />
          <Button startIcon={<RefreshIcon />} onClick={() => refetchPreview()}>Refresh</Button>
        </Stack>
        {preview?.meta && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Source: {preview.meta.catalogSource || preview.source || 'default'}
            {preview.meta.eventName ? ` · ${preview.meta.eventName}` : ''}
            {preview.meta.restockAt ? ` · Restock: ${preview.meta.restockAt}` : ''}
          </Alert>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Deal</TableCell>
              <TableCell>Cost</TableCell>
              <TableCell>Tab</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(preview?.deals || []).map((d, i) => (
              <TableRow key={d.dealTemplateId || d.id || i}>
                <TableCell>
                  <DealAssetSummary amount={d.amount} assetId={d.assetId} assetMap={assetMap} fallbackId={d.assetId} />
                </TableCell>
                <TableCell>
                  <DealAssetSummary amount={d.priceAmount} assetId={d.priceAssetId} assetMap={assetMap} fallbackId={d.priceAssetId} />
                </TableCell>
                <TableCell>{traderTabLabel(d) || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TabPanel>

      <Dialog
        open={addDefaultDealDialog.open}
        onClose={() => setAddDefaultDealDialog({ open: false, deal: null, tab: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add deal to default set
          {addDefaultDealDialog.tab ? ` — ${addDefaultDealDialog.tab}` : ''}
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            options={addDefaultDealDialog.tab
              ? addableDefaultDealsForTab(addDefaultDealDialog.tab)
              : addableDefaultDeals}
            getOptionLabel={dealOptionLabel}
            value={addDefaultDealDialog.deal}
            onChange={(_, deal) => setAddDefaultDealDialog((s) => ({ ...s, deal }))}
            renderInput={(params) => <TextField {...params} label="Deal from library" sx={{ mt: 1 }} />}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Stack spacing={0.5}>
                  <DealAssetSummary amount={option.amount} assetId={option.assetId} assetMap={assetMap} />
                  <DealAssetSummary amount={option.priceAmount} assetId={option.priceAssetId} assetMap={assetMap} />
                </Stack>
              </li>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDefaultDealDialog({ open: false, deal: null, tab: null })}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!addDefaultDealDialog.deal}
            onClick={() => addDefaultEntry(addDefaultDealDialog.deal, addDefaultDealDialog.tab)}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dealDialog.open} onClose={() => setDealDialog({ open: false, deal: emptyDeal, editing: false })} maxWidth="sm" fullWidth>
        <DialogTitle>{dealDialog.editing ? 'Edit deal' : 'New deal'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select label="Category" value={dealDialog.deal.catalogKind || 'trader'}
                onChange={(e) => setDealDialog((s) => ({
                  ...s,
                  deal: {
                    ...s.deal,
                    catalogKind: e.target.value,
                    merchandiseType: e.target.value === 'moani_board' ? 'MoaniBoard' : (s.deal.merchandiseType === 'MoaniBoard' ? 'Other' : s.deal.merchandiseType),
                  },
                }))}>
                <MenuItem value="trader">Trader NPC</MenuItem>
                <MenuItem value="moani_board">Moani Board</MenuItem>
                <MenuItem value="both">Both</MenuItem>
              </Select>
            </FormControl>
            {dealDialog.deal.catalogKind !== 'moani_board' && (
              <FormControl fullWidth>
                <InputLabel>Trader tab</InputLabel>
                <Select
                  label="Trader tab"
                  value={TRADER_TAB_TYPES.includes(dealDialog.deal.merchandiseType)
                    ? dealDialog.deal.merchandiseType
                    : 'Other'}
                  onChange={(e) => setDealDialog((s) => ({ ...s, deal: { ...s.deal, merchandiseType: e.target.value } }))}
                >
                  {TRADER_TAB_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            {dealDialog.deal.catalogKind === 'moani_board' && (
              <Alert severity="info">Moani Board deals use tab MoaniBoard automatically.</Alert>
            )}
            <Autocomplete
              options={selectableTraderAssets}
              value={selectedSoldAsset}
              onChange={(_, v) => setDealDialog((s) => ({ ...s, deal: { ...s.deal, assetId: v?.id || '' } }))}
              getOptionLabel={(option) => getAssetPickerLabel(option)}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              filterOptions={filterAssetOptions}
              renderOption={renderAssetOption}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sold item (reward)"
                  placeholder="Search by name or type…"
                  helperText={TRADER_ASSET_PICKER_HELPER}
                />
              )}
            />
            {renderAssetPreview(selectedSoldAsset)}
            <TextField type="number" label="Sold amount" value={dealDialog.deal.amount}
              onChange={(e) => setDealDialog((s) => ({ ...s, deal: { ...s.deal, amount: Number(e.target.value) } }))} />
            <Autocomplete
              options={selectableTraderAssets}
              value={selectedCostAsset}
              onChange={(_, v) => setDealDialog((s) => ({ ...s, deal: { ...s.deal, priceAssetId: v?.id || '' } }))}
              getOptionLabel={(option) => getAssetPickerLabel(option)}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              filterOptions={filterAssetOptions}
              renderOption={renderAssetOption}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cost item"
                  placeholder="Search by name or type…"
                  helperText={TRADER_ASSET_PICKER_HELPER}
                />
              )}
            />
            {renderAssetPreview(selectedCostAsset)}
            <TextField type="number" label="Cost amount" value={dealDialog.deal.priceAmount}
              onChange={(e) => setDealDialog((s) => ({ ...s, deal: { ...s.deal, priceAmount: Number(e.target.value) } }))} />
            <TextField type="number" label="Stack per restock" value={dealDialog.deal.defaultStackAmount}
              onChange={(e) => setDealDialog((s) => ({ ...s, deal: { ...s.deal, defaultStackAmount: Number(e.target.value) } }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDealDialog({ open: false, deal: emptyDeal, editing: false })}>Cancel</Button>
          <Button variant="contained" onClick={() => saveDealMutation.mutate(dealDialog.deal)}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={eventDialog.open} onClose={() => setEventDialog((s) => ({ ...s, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>New event catalog</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Event name" value={eventDialog.name} onChange={(e) => setEventDialog((s) => ({ ...s, name: e.target.value }))} fullWidth />
            <TextField type="date" label="Start (UTC)" InputLabelProps={{ shrink: true }} value={eventDialog.startDate}
              onChange={(e) => setEventDialog((s) => ({ ...s, startDate: e.target.value }))} fullWidth />
            <TextField type="date" label="End (UTC)" InputLabelProps={{ shrink: true }} value={eventDialog.endDate}
              onChange={(e) => setEventDialog((s) => ({ ...s, endDate: e.target.value }))} fullWidth />
            <TextField type="number" label="Priority" value={eventDialog.priority}
              onChange={(e) => setEventDialog((s) => ({ ...s, priority: Number(e.target.value) }))} fullWidth />
            <Autocomplete multiple options={deals.filter((d) => d.isActive && (
              selectedTrader?.catalogKind === 'moani_board'
                ? d.catalogKind === 'moani_board' || d.catalogKind === 'both'
                : d.catalogKind === 'trader' || d.catalogKind === 'both'
            ))} getOptionLabel={dealOptionLabel}
              value={deals.filter((d) => eventDialog.dealTemplateIds.includes(d.id))}
              onChange={(_, val) => setEventDialog((s) => ({ ...s, dealTemplateIds: val.map((v) => v.id) }))}
              renderInput={(params) => <TextField {...params} label="Event deals" />} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventDialog((s) => ({ ...s, open: false }))}>Cancel</Button>
          <Button variant="contained" onClick={() => saveEventMutation.mutate({
            gridObjectId,
            name: eventDialog.name,
            startDate: eventDialog.startDate,
            endDate: eventDialog.endDate,
            priority: eventDialog.priority,
            entries: eventDialog.dealTemplateIds.map((id) => ({ dealTemplateId: id })),
          })}>Create (draft)</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importDialog.open}
        onClose={() => {
          setImportDialog((s) => ({ ...s, open: false }));
          setImportPreview(null);
          setImportPreviewError('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import trader catalog to draft</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              Imports into the current environment ({isDevelopment ? 'Development' : 'Production'}) as draft only.
              Review, then publish. Existing draft default sets and event catalogs for matched traders will be replaced.
            </Alert>
            <Typography variant="body2">
              File: <strong>{importDialog.fileName || '—'}</strong>
            </Typography>
            {importDialog.bundle && (
              <Typography variant="body2" color="text.secondary">
                Source: {importDialog.bundle.environment || 'unknown env'} · scope {importDialog.bundle.scope || '—'}
                {' · '}{importDialog.bundle.dealTemplates?.length || 0} deals
                {' · '}{importDialog.bundle.catalogs?.length || 0} traders
              </Typography>
            )}
            <FormControlLabel
              control={(
                <Checkbox
                  checked={importDialog.importDeals}
                  onChange={(e) => setImportDialog((s) => ({ ...s, importDeals: e.target.checked }))}
                />
              )}
              label="Import deal library (upsert by deal id)"
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={importDialog.importDefaultSets}
                  onChange={(e) => setImportDialog((s) => ({ ...s, importDefaultSets: e.target.checked }))}
                />
              )}
              label="Replace draft default sets"
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={importDialog.importEventCatalogs}
                  onChange={(e) => setImportDialog((s) => ({ ...s, importEventCatalogs: e.target.checked }))}
                />
              )}
              label="Replace draft event catalogs"
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={importDialog.selectedTraderOnly}
                  disabled={!gridObjectId}
                  onChange={(e) => setImportDialog((s) => ({ ...s, selectedTraderOnly: e.target.checked }))}
                />
              )}
              label={gridObjectId ? `Import only selected trader (${selectedTrader?.displayName})` : 'Import only selected trader (select a trader first)'}
            />

            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Import preview (current draft vs file)
              </Typography>

              {importPreviewLoading && (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!importPreviewLoading && importPreviewError && (
                <Alert severity="error">{importPreviewError}</Alert>
              )}

              {!importPreviewLoading && !importPreviewError && importPreview && (
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {importDialog.importDeals && (
                      <>
                        <Chip label={`${importPreview.summary.dealsNew} New deals`} color="success" size="small" />
                        <Chip label={`${importPreview.summary.dealsUpdated} Updated deals`} color="warning" size="small" />
                        <Chip label={`${importPreview.summary.dealsUnchanged} Unchanged deals`} size="small" />
                      </>
                    )}
                    {importDialog.importDefaultSets && importPreview.summary.defaultSetsChanged > 0 && (
                      <Chip
                        label={`${importPreview.summary.defaultSetsChanged} Stock changes`}
                        color="info"
                        size="small"
                      />
                    )}
                    {importDialog.importEventCatalogs && (
                      <>
                        {importPreview.summary.eventsNew > 0 && (
                          <Chip label={`${importPreview.summary.eventsNew} New events`} color="success" size="small" />
                        )}
                        {importPreview.summary.eventsUpdated > 0 && (
                          <Chip label={`${importPreview.summary.eventsUpdated} Updated events`} color="warning" size="small" />
                        )}
                        {importPreview.summary.eventsRemoved > 0 && (
                          <Chip label={`${importPreview.summary.eventsRemoved} Removed events`} color="error" size="small" />
                        )}
                      </>
                    )}
                  </Stack>

                  {importPreview.diff.missingTraders.length > 0 && (
                    <Alert severity="warning">
                      Traders not found on target and will be skipped:
                      {' '}{importPreview.diff.missingTraders.join(', ')}
                    </Alert>
                  )}

                  <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
                    {importDialog.importDeals && importPreview.diff.deals.new.length > 0 && (
                      <Box mb={2}>
                        <Typography variant="caption" fontWeight="bold" color="success.main">
                          NEW DEALS ({importPreview.diff.deals.new.length})
                        </Typography>
                        <List dense>
                          {importPreview.diff.deals.new.map((item) => (
                            <ListItem key={item.id} sx={{ py: 0 }}>
                              <ListItemText
                                primary={item.title}
                                secondary={item.merchandiseType}
                                primaryTypographyProps={{ variant: 'body2' }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    {importDialog.importDeals && importPreview.diff.deals.updated.length > 0 && (
                      <Box mb={2}>
                        <Typography variant="caption" fontWeight="bold" color="warning.main">
                          UPDATED DEALS ({importPreview.diff.deals.updated.length})
                        </Typography>
                        <List dense>
                          {importPreview.diff.deals.updated.map((item) => (
                            <ListItem key={item.id} sx={{ py: 0 }}>
                              <ListItemText
                                primary={item.title}
                                secondary={(
                                  <Box>
                                    {item.changes.map((change, idx) => (
                                      <Typography key={idx} variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>
                                        {change.field}: {String(change.from ?? '—')} → {String(change.to ?? '—')}
                                      </Typography>
                                    ))}
                                  </Box>
                                )}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    {importDialog.importDeals && importPreview.diff.deals.unchanged.length > 0 && (
                      <Box mb={2}>
                        <Typography variant="caption" fontWeight="bold" color="text.secondary">
                          UNCHANGED DEALS ({importPreview.diff.deals.unchanged.length})
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {importPreview.diff.deals.unchanged.map((item) => item.title).join(', ')}
                        </Typography>
                      </Box>
                    )}

                    {importPreview.diff.traders.map((trader) => {
                      const defaultSet = trader.defaultSet;
                      const hasDefaultChanges = importDialog.importDefaultSets
                        && defaultSet
                        && !defaultSet.unchanged
                        && (defaultSet.added.length || defaultSet.removed.length || defaultSet.changed.length);
                      const hasEventChanges = importDialog.importEventCatalogs
                        && (trader.events.new.length || trader.events.updated.length || trader.events.removed.length);
                      if (!hasDefaultChanges && !hasEventChanges) return null;

                      return (
                        <Box key={trader.gridObjectId} mb={2}>
                          <Typography variant="body2" fontWeight="bold" gutterBottom>
                            {trader.displayName}
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              ({trader.gridObjectId})
                            </Typography>
                          </Typography>

                          {hasDefaultChanges && (
                            <Box sx={{ pl: 1, mb: 1 }}>
                              <Typography variant="caption" fontWeight="bold" color="info.main" display="block">
                                DEFAULT STOCK ({defaultSet.currentCount} → {defaultSet.incomingCount})
                              </Typography>
                              {defaultSet.added.length > 0 && (
                                <Typography variant="caption" color="success.main" display="block">
                                  + {defaultSet.added.map((entry) => entry.title).join(', ')}
                                </Typography>
                              )}
                              {defaultSet.removed.length > 0 && (
                                <Typography variant="caption" color="error.main" display="block">
                                  − {defaultSet.removed.map((entry) => entry.title).join(', ')}
                                </Typography>
                              )}
                              {defaultSet.changed.length > 0 && (
                                <List dense disablePadding>
                                  {defaultSet.changed.map((entry) => (
                                    <ListItem key={entry.dealTemplateId} sx={{ py: 0, pl: 0 }}>
                                      <ListItemText
                                        primary={entry.title}
                                        secondary={entry.changes.map((change) => (
                                          `${change.field}: ${String(change.from ?? '—')} → ${String(change.to ?? '—')}`
                                        )).join(' · ')}
                                        primaryTypographyProps={{ variant: 'caption' }}
                                        secondaryTypographyProps={{ variant: 'caption', sx: { fontFamily: 'monospace' } }}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              )}
                            </Box>
                          )}

                          {importDialog.importEventCatalogs && trader.events.new.length > 0 && (
                            <Box sx={{ pl: 1, mb: 1 }}>
                              <Typography variant="caption" fontWeight="bold" color="success.main" display="block">
                                NEW EVENTS ({trader.events.new.length})
                              </Typography>
                              {trader.events.new.map((event) => (
                                <Typography key={event.id || `${event.name}-${event.startDate}`} variant="caption" display="block">
                                  {event.name} ({event.startDate} – {event.endDate}) · {event.dealCount} deals
                                </Typography>
                              ))}
                            </Box>
                          )}

                          {importDialog.importEventCatalogs && trader.events.updated.length > 0 && (
                            <Box sx={{ pl: 1, mb: 1 }}>
                              <Typography variant="caption" fontWeight="bold" color="warning.main" display="block">
                                UPDATED EVENTS ({trader.events.updated.length})
                              </Typography>
                              <List dense disablePadding>
                                {trader.events.updated.map((event) => (
                                  <ListItem key={event.id} sx={{ py: 0, pl: 0 }}>
                                    <ListItemText
                                      primary={event.name}
                                      secondary={event.changes.map((change) => (
                                        `${change.field}: ${String(change.from ?? '—')} → ${String(change.to ?? '—')}`
                                      )).join(' · ')}
                                      primaryTypographyProps={{ variant: 'caption' }}
                                      secondaryTypographyProps={{ variant: 'caption', sx: { fontFamily: 'monospace' } }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}

                          {importDialog.importEventCatalogs && trader.events.removed.length > 0 && (
                            <Box sx={{ pl: 1, mb: 1 }}>
                              <Typography variant="caption" fontWeight="bold" color="error.main" display="block">
                                REMOVED EVENTS ({trader.events.removed.length})
                              </Typography>
                              {trader.events.removed.map((event) => (
                                <Typography key={event.id} variant="caption" display="block">
                                  {event.name} ({event.startDate} – {event.endDate})
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setImportDialog((s) => ({ ...s, open: false }));
            setImportPreview(null);
            setImportPreviewError('');
          }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              !importDialog.bundle
              || importCatalogMutation.isPending
              || importPreviewLoading
              || !!importPreviewError
            }
            onClick={() => importCatalogMutation.mutate({
              bundle: importDialog.bundle,
              importDeals: importDialog.importDeals,
              importDefaultSets: importDialog.importDefaultSets,
              importEventCatalogs: importDialog.importEventCatalogs,
              gridObjectId: importDialog.selectedTraderOnly ? gridObjectId : null,
            })}
          >
            Import to draft
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TraderCatalogManager;
